import { useId, useRef, useEffect, useState, KeyboardEvent, MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { FilterDef, FilterValues } from './chip-filters.types';
import ValuePicker from './value-picker.component';
import styles from './chip-filters.module.scss';

interface Props {
  def: FilterDef;
  value: FilterValues;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onChange: (key: string, next: string | string[] | undefined) => void;
}

function getDisplayValue(
  def: FilterDef,
  val: string | string[] | undefined,
  t: (k: string, o?: Record<string, unknown>) => string
): string | null {
  if (!val || (Array.isArray(val) && val.length === 0)) return null;
  if (Array.isArray(val)) {
    if (val.length === 1) return def.options.find((o) => o.value === val[0])?.label ?? val[0];
    return t('chip-filters.selected', { count: val.length });
  }
  return def.options.find((o) => o.value === val)?.label ?? val;
}

export default function Chip({ def, value, isOpen, onOpen, onClose, onChange }: Props) {
  const { t } = useTranslation();
  const disabledHintId = useId();
  const chipRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [hintPos, setHintPos] = useState<{ top: number; left: number } | null>(null);
  const current = value[def.key];
  const displayValue = getDisplayValue(def, current, t);
  const isActive = !!displayValue;
  const isDisabled = def.disabled && !isActive;

  // Click outside: close if click is not in chip or popover portal
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: globalThis.MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (chipRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('touchstart', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('touchstart', handleMouseDown);
    };
  }, [isOpen, onClose]);

  // Compute popover position when opening; keep in sync on scroll/resize
  useEffect(() => {
    if (!isOpen || !chipRef.current) {
      setPopoverPos(null);
      return;
    }
    const updatePos = () => {
      if (!chipRef.current) return;
      const rect = chipRef.current.getBoundingClientRect();
      setPopoverPos({ top: rect.bottom + 6, left: rect.left });
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (isDisabled) return;
    if (isOpen) {
      onClose();
    } else {
      onOpen();
      chipRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      onOpen();
    }
  };

  const handleClear = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onChange(def.key, undefined);
    if (isOpen) onClose();
  };

  const handleHintEnter = () => {
    if (!isDisabled || !def.disabledHint || !chipRef.current) return;
    const rect = chipRef.current.getBoundingClientRect();
    setHintPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 });
  };

  const handleHintLeave = () => setHintPos(null);

  return (
    <div ref={chipRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <div
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        className={clsx(
          styles.chip,
          isActive && styles.chipActive,
          isOpen && !isDisabled && styles.chipOpen,
          isDisabled && styles.chipDisabled
        )}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        onMouseEnter={handleHintEnter}
        onMouseLeave={handleHintLeave}
        onFocus={handleHintEnter}
        onBlur={handleHintLeave}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-disabled={isDisabled}
        aria-label={def.label}
        aria-describedby={isDisabled && def.disabledHint ? disabledHintId : undefined}
      >
        <span className={styles.chipIcon}>{def.icon}</span>
        <span className={styles.chipLabel}>{def.label}</span>

        {isActive ? (
          <>
            <span className={styles.chipValuePrefix}>{t('chip-filters.valueSeparator')}</span>
            <span className={styles.chipValue}>{displayValue}</span>
            <button
              type="button"
              className={styles.chipClearBtn}
              onClick={handleClear}
              aria-label={t('chip-filters.clearFilter', { label: def.label })}
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 9 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M1 1L8 8M8 1L1 8"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </>
        ) : (
          <span className={clsx(styles.chipCaret, isOpen && styles.chipCaretOpen)}>
            <i className="ki-duotone ki-down fs-9">
              <span className="path1" />
            </i>
          </span>
        )}
      </div>

      {/* Disabled hint via portal so no queda recortado por overflow del scroll wrapper */}
      {isDisabled &&
        def.disabledHint &&
        hintPos &&
        createPortal(
          <span
            id={disabledHintId}
            className={styles.disabledHint}
            style={{
              position: 'fixed',
              top: hintPos.top,
              left: hintPos.left,
              opacity: 1,
              transform: 'translate(-50%, 0)',
              pointerEvents: 'none'
            }}
          >
            {def.disabledHint}
          </span>,
          document.body
        )}

      {isOpen &&
        !isDisabled &&
        popoverPos &&
        createPortal(
          <div
            ref={popoverRef}
            style={{ position: 'fixed', top: popoverPos.top, left: popoverPos.left, zIndex: 1055 }}
          >
            <div className={styles.popover}>
              <div className={styles.pickerHeader}>
                <span className={styles.pickerHeaderIcon}>{def.icon}</span>
                <span className={styles.pickerHeaderLabel}>{def.label}</span>
              </div>
              <ValuePicker
                def={def}
                current={current}
                onApply={(next) => onChange(def.key, next)}
                onClose={onClose}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
